import { organizationQueryKey } from '@cast/constants';
import { Organization, OrganizationType } from '@cast/types';

import { PRODUCT_BASE_PATHS } from '@cast/console-constants';

export const ORGANIZATION_MANAGEMENT_BASE_PATH = `${PRODUCT_BASE_PATHS.settings}/management`;

/** Classic console default landing path after org switch (hard reload). */
export const DEFAULT_ORGANIZATION_SWITCH_PATH = '/dashboard';

type BuildOrganizationSwitchUrlArgs = {
  organizationId: string;
  path?: string;
};

/**
 * Builds a hard-navigation URL used when switching organization outside
 * `/organization` routes. Clears SPA React Query cache via full page load.
 */
export const buildOrganizationSwitchUrl = ({
  organizationId,
  path = DEFAULT_ORGANIZATION_SWITCH_PATH,
}: BuildOrganizationSwitchUrlArgs): string => {
  const url = new URL(path, window.location.origin);
  url.searchParams.set(organizationQueryKey, organizationId);
  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * Derives the enterprise organization ID from any organization.
 * - For ENTERPRISE orgs: returns the org's own ID
 * - For CHILD orgs: returns the parent's ID
 * - For DEFAULT orgs: returns undefined
 */
export const getEnterpriseIdFromOrg = (
  org: Organization | undefined | null
): string | undefined => {
  if (!org) return undefined;

  if (org.type === OrganizationType.ENTERPRISE) {
    return org.id;
  }

  if (org.type === OrganizationType.CHILD) {
    return org.parentId ?? undefined;
  }

  return undefined;
};

/**
 * Builds an enterprise route URL with optional organization query parameter.
 *
 * @param enterpriseId - The enterprise organization ID
 * @param path - The path segment (e.g., 'overview', 'dashboard', 'manage-users')
 * @param orgId - Optional child organization ID to add as query parameter
 * @returns Full URL path with query params if provided
 *
 * @example
 * buildEnterpriseUrl('ent-123', 'overview')
 * // => '/organization/enterprise/ent-123/overview'
 *
 * buildEnterpriseUrl('ent-123', 'dashboard', 'child-456')
 * // => '/organization/enterprise/ent-123/dashboard?org=child-456'
 */
export const buildEnterpriseUrl = (
  enterpriseId: string,
  path: string,
  orgId?: string
): string => {
  const base = `${PRODUCT_BASE_PATHS.settings}/enterprise/${enterpriseId}/${path}`;
  return orgId ? `${base}?org=${orgId}` : base;
};

/**
 * Builds a management route URL with optional organization query parameter.
 *
 * @param path - The path segment (e.g., 'dashboard', 'billing-report')
 * @param orgId - Optional organization ID to add as query parameter
 */
export const buildManagementUrl = (path: string, orgId?: string): string => {
  const base = `${ORGANIZATION_MANAGEMENT_BASE_PATH}/${path}`;
  return orgId ? `${base}?org=${orgId}` : base;
};

/**
 * Route configuration map for organization types.
 * Maps organization type to a function that generates the correct redirect URL.
 *
 * BACKWARDS COMPATIBILITY NOTE:
 * - CHILD orgs prefer enterprise routes but can also access management routes
 * - This allows existing bookmarks/links with /organization/management/*?org=childId to work
 * - New navigation should always generate enterprise routes for CHILD orgs (preferred)
 * - The route validation hook allows CHILD orgs on both route types without redirecting
 */
export const ORGANIZATION_ROUTE_CONFIG = {
  [OrganizationType.ENTERPRISE]: (org: Organization) =>
    buildEnterpriseUrl(org.id, 'overview', org.id),

  [OrganizationType.CHILD]: (org: Organization) => {
    if (!org.parentId) {
      console.error('Child organization missing parentId:', org.id);
      return buildManagementUrl('dashboard', org.id);
    }
    return buildEnterpriseUrl(org.parentId, 'overview', org.id);
  },

  [OrganizationType.DEFAULT]: (org: Organization) =>
    buildManagementUrl('dashboard', org.id),
} as const;

/**
 * Gets the default route for an organization based on its type.
 */
export const getOrganizationDefaultRoute = (org: Organization): string => {
  const routeBuilder = ORGANIZATION_ROUTE_CONFIG[org.type];
  return routeBuilder
    ? routeBuilder(org)
    : buildManagementUrl('dashboard', org.id);
};

/**
 * Extracts the sub-path segment from an enterprise URL.
 * e.g. '/organization/enterprise/ent-123/access-control/users' → 'access-control/users'
 */
export const getEnterpriseSubPath = (
  pathname: string,
  enterpriseId: string
): string | undefined => {
  const prefix = `${PRODUCT_BASE_PATHS.settings}/enterprise/${enterpriseId}/`;

  if (!pathname.startsWith(prefix)) {
    return undefined;
  }

  return pathname.slice(prefix.length) || undefined;
};
